import React, { useEffect, useState } from "react";
import "./AdminDashboard.css";
import bfLogo from "../assets/bf_logo.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* ===== FIREBASE ===== */
import { auth, db } from "../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";

function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            setUserData(data);
            
            if (data.subscriptionStatus === "trial" && data.trialEndsAt) {
              const trialEnd = data.trialEndsAt.toDate().getTime();
              const diffDays = Math.ceil((trialEnd - Date.now()) / (1000 * 60 * 60 * 24));
              if (diffDays <= 0) {
                alert(t("trial_ended_msg") || "Your trial has expired.");
                navigate("/");
                return;
              }
              if (diffDays <= 5) {
                const wantToUpgrade = window.confirm(`${t("trial_warning_prefix") || "Trial ends in"} ${diffDays} ${t("days")}. Upgrade now?`);
                if (wantToUpgrade) navigate("/");
              }
            }
          }
        } catch (error) { console.error(error); }
      } else {
        navigate("/admin/login");
      }
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, [navigate, t]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubSession = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      const data = snap.data();
      const localSessionId = localStorage.getItem("activeSessionId");
      if (data?.activeSessionId && localSessionId && data.activeSessionId !== localSessionId) {
        alert(t("session_conflict_msg") || "This account is logged in on another device. You will be logged out.");
        signOut(auth);
        navigate("/admin/login");
      }
    });
    return () => unsubSession();
  }, [currentUser, navigate, t]);

  const LockBadge = () => (
    <div style={{
      position: "absolute",
      top: "10px",
      right: "10px",
      zIndex: 10
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM7 11V7a5 5 0 0 1 10 0v4" 
              stroke="#ffd976" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );

  const goldIcon = (path) => (
    <svg width="38" height="38" viewBox="0 0 24 24" fill="none">
      <defs><linearGradient id="gold_grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ffd976" /><stop offset="100%" stopColor="#d08a00" /></linearGradient></defs>
      <path d={path} stroke="url(#gold_grad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const icons = {
    workers: goldIcon("M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm6 8v-1a5 5 0 0 0-10 0v1Z"),
    projects: goldIcon("M3 7h18M3 7l2-3h6l2 3m-10 0v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7"),
    weeklyPlan: goldIcon("M3 10h18M8 3v4M16 3v4M3 5h18v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5"),
    workHours: goldIcon("M12 7v5l3 2M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9Z"),
    documents: goldIcon("M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h5"),
    acts: goldIcon("M6 3h12v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V3Zm3 6h6M9 12h6"),
    googleMaps: goldIcon("M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5Z"),
    fahrtenbuch: goldIcon("M5 17h14M5 10h14l-1-3H6l-1 3Zm3 7h8a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3H8a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3Z"),
    settings: goldIcon("M12 15a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8-3a1 1 0 0 0-.76-.97l-1.52-.38a6.94 6.94 0 0 0-.84-2l.9-1.36a1 1 0 0 0-.12-1.28l-1.42-1.42a1 1 0 0 0-1.28-.12l-1.36.9a6.94 6.94 0 0 0-2-.84L13.97 4a1 1 0 0 0-.97-.76h-1.94a1 1 0 0 0-.97.76l-.38 1.52a6.94 6.94 0 0 0-2 .84l-1.36-.9a6.94 6.94 0 0 0-1.28.12L3.65 7.12a1 1 0 0 0-.12 1.28l.9 1.36a6.94 6.94 0 0 0-.84 2L2.17 12a1 1 0 0 0-.76.97v1.94a1 1 0 0 0 .76.97l1.52.38a6.94 6.94 0 0 0 .84 2l-.9 1.36a1 1 0 0 0 .12 1.28l1.42 1.42a1 1 0 0 0 1.28.12l1.36-.9a6.94 6.94 0 0 0 2 .84l.38 1.52a1 1 0 0 0 .97.76h1.94a1 1 0 0 0 .97-.76l.38-1.52a6.94 6.94 0 0 0 2-.84l1.36.9a6.94 6.94 0 0 0 1.28-.12l1.42-1.42a1 1 0 0 0 .12-1.28l-.9-1.36a6.94 6.94 0 0 0 .84-2l1.52-.38a1 1 0 0 0 .76-.97Z"),
    chat: goldIcon("M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-11.2h.8a8.38 8.38 0 0 1 3.8.9L21 3z"),
    library: goldIcon("M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm12.5 9l-6-6-4 4-2-2-5 5"),
    warehouse: goldIcon("M21 8v13H3V8M1 3l11-2 11 2v5H1V3zm11 10h.01")
  };

  const tiles = [
    { label: t("weekly_plan"), icon: icons.weeklyPlan, path: "/admin/weeklyplan" },
    { label: t("projects"), icon: icons.projects, path: "/admin/projects" },
    { label: t("workers"), icon: icons.workers, path: "/admin/workers" },
    { label: t("workHours"), icon: icons.workHours, path: "/admin/workhours" },
    { label: t("documents"), icon: icons.documents, path: "/admin/documents" },
    
    // --- SUKEISTA: REPORTS KELIAUJA ČIA (VIETOJ ACTS) ---
    // Taip pat pataisytas raktas į t("reports_title")
    { label: t("reports_title"), icon: icons.library, path: "/admin/beweissicherung" }, 
    
    // Pakeistas pavadinimas čia:
    { label: "GPS Maps", icon: icons.googleMaps, path: "/admin/maps" },

    { label: t("fahrtenbuch"), icon: icons.fahrtenbuch, path: "pro", isPro: true },
    { label: t("warehouse"), icon: icons.warehouse, path: "pro", isPro: true },
    { label: t("chat"), icon: icons.chat, path: "pro", isPro: true },

    // --- SUKEISTA: ACTS KELIAUJA ČIA (Į APAČIĄ) ---
    { label: t("acts"), icon: icons.acts, path: "pro", isPro: true },
    
    { label: t("settings"), icon: icons.settings, path: "/admin/settings" }
  ];

  const handleTileClick = (item) => {
    if (item.isPro) {
      alert("This module will be available in version Buildforce AL Pro");
    } else {
      navigate(item.path);
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-wrapper" style={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      padding: "40px 20px",
      boxSizing: "border-box"
    }}>
      <button 
        className="back-btn" 
        onClick={() => navigate("/admin/login")}
        style={{ position: "absolute", top: "30px", left: "30px", zIndex: 100 }}
      >
        ← {t("back")}
      </button>

      <img src={bfLogo} alt="BF Logo" className="admin-logo" style={{ maxWidth: "120px", marginBottom: "10px" }} />
      <h1 className="admin-title" style={{ marginBottom: "30px", textAlign: "center" }}>{t("admin_dashboard")}</h1>
      
      <div className="admin-grid">
        {tiles.map((item, i) => (
          <div 
            key={i} 
            className="admin-tile" 
            onClick={() => handleTileClick(item)} 
            style={{ 
              position: "relative",
              opacity: item.isPro ? 0.45 : 1,
              filter: item.isPro ? "grayscale(0.8)" : "none"
            }}
          >
            {item.isPro && <LockBadge />}
            <div className="admin-icon">{item.icon}</div>
            <div className="admin-label">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;